import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { useApp } from '@/app/AppContext'
import { Icon } from '@/components/Icon'
import { ApiError } from '@/lib/api'
import { ConfirmModal, StateView } from './record'
import {
  deletePhoto,
  makeMainPhoto,
  reorderPhotos,
  uploadPhoto,
  usePhotoUrl,
  type ProductPhoto,
} from './productApi'

/**
 * Галерея товара: главное фото крупно, остальные миниатюрами (V60).
 *
 * ## Что здесь главное
 *
 * **Непроверенное антивирусом фото — заглушка «проверяется», а не битая картинка.**
 * Сервер не отдаёт файл до вердикта ClamAV (404), и `<img>` на его месте показал бы
 * сломанный значок, который читается как «фото потерялось». Заглушка говорит, что фото
 * есть и скоро будет; заражённое — что его не будет.
 *
 * **Загрузка нескольких — по одному запросу на файл**, а отказ по одному файлу называется
 * поимённо и не отменяет остальные: из пяти фото одно, оказавшееся PDF-ом, не должно
 * оставить товар без четырёх нормальных.
 *
 * **Проверка «это изображение» — на сервере, по содержимому.** `accept` у поля выбора
 * только подсказывает диалогу: файл, переименованный в `.jpg`, он пропустит, а сервер нет.
 */
export function ProductGallery({ productId, photos, mayEdit, maxPhotoBytes, maxPhotos, onChanged }: {
  productId: string
  photos: ProductPhoto[]
  mayEdit: boolean
  maxPhotoBytes?: number
  maxPhotos?: number
  onChanged: (photos?: ProductPhoto[]) => void
}) {
  const { t } = useApp()
  const queryClient = useQueryClient()
  const input = useRef<HTMLInputElement>(null)
  const [failures, setFailures] = useState<string[]>([])
  const [uploading, setUploading] = useState(0)
  const [removing, setRemoving] = useState<ProductPhoto | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const main = photos.find((photo) => photo.main) ?? photos[0] ?? null
  const shown = photos.find((photo) => photo.id === selectedId) ?? main

  const changed = (next?: ProductPhoto[]) => {
    void queryClient.invalidateQueries({ queryKey: ['product-card', productId] })
    onChanged(next)
  }
  const reason = (error: unknown) => (error instanceof ApiError ? error.message : t('app.error'))

  const upload = async (files: FileList | null) => {
    if (!files || files.length === 0) return
    setFailures([])
    const list = Array.from(files)
    setUploading(list.length)
    const failed: string[] = []
    for (const file of list) {
      try {
        await uploadPhoto(productId, file)
      } catch (error) {
        failed.push(`${file.name}: ${reason(error)}`)
      }
      setUploading((left) => left - 1)
    }
    setFailures(failed)
    if (input.current) input.current.value = ''
    changed()
  }

  const setMain = useMutation({
    mutationFn: (photo: ProductPhoto) => makeMainPhoto(productId, photo.id),
    onSuccess: (next) => changed(next),
    onError: (error) => setFailures([reason(error)]),
  })
  const reorder = useMutation({
    mutationFn: (ids: string[]) => reorderPhotos(productId, ids),
    onSuccess: (next) => changed(next),
    onError: (error) => setFailures([reason(error)]),
  })
  const remove = useMutation({
    mutationFn: (photo: ProductPhoto) => deletePhoto(productId, photo.id),
    onSuccess: (next) => {
      setRemoving(null)
      setSelectedId(null)
      changed(next)
    },
    onError: (error) => {
      setRemoving(null)
      setFailures([reason(error)])
    },
  })

  const move = (photo: ProductPhoto, delta: number) => {
    const ids = photos.map((candidate) => candidate.id)
    const from = ids.indexOf(photo.id)
    const to = from + delta
    if (from < 0 || to < 0 || to >= ids.length) return
    ids.splice(from, 1)
    ids.splice(to, 0, photo.id)
    reorder.mutate(ids)
  }

  const full = maxPhotos != null && photos.length >= maxPhotos
  const busy = uploading > 0 || setMain.isPending || reorder.isPending || remove.isPending

  return (
    <div className="stack" style={{ gap: 12 }}>
      {mayEdit && (
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <input
            ref={input}
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp"
            hidden
            aria-label={t('product.photo.choose')}
            onChange={(event) => void upload(event.target.files)}
          />
          <button type="button" className="btn btn-secondary" disabled={busy || full}
                  onClick={() => input.current?.click()}>
            <Icon name="upload" />
            {uploading > 0 ? t('product.photo.uploading', { count: uploading }) : t('product.photo.upload')}
          </button>
          <span className="t-sm muted">
            {full
              ? t('product.photo.full', { max: maxPhotos ?? 0 })
              : t('product.photo.rules', { mb: Math.round((maxPhotoBytes ?? 10485760) / 1048576) })}
          </span>
        </div>
      )}

      {failures.length > 0 && (
        <div className="alert alert-danger" role="alert">
          <Icon name="alert" />
          <span className="fg">
            {failures.map((line) => (
              <span key={line} style={{ display: 'block' }}>{line}</span>
            ))}
          </span>
        </div>
      )}

      {photos.length === 0 ? (
        <StateView compact kind="empty" title={t('product.photo.empty')}
                   description={mayEdit ? t('product.photo.emptyText') : undefined} />
      ) : (
        <>
          {shown && <PhotoFrame photo={shown} large />}
          <ul className="pg-strip" aria-label={t('product.photo.list')}>
            {photos.map((photo, index) => (
              <li key={photo.id} className={photo.id === shown?.id ? 'pg-item is-current' : 'pg-item'}>
                <button type="button" className="pg-thumb" aria-pressed={photo.id === shown?.id}
                        aria-label={t('product.photo.show', { name: photo.fileName })}
                        onClick={() => setSelectedId(photo.id)}>
                  <PhotoFrame photo={photo} />
                </button>
                <div className="pg-meta">
                  {photo.main ? (
                    <span className="badge b-success"><Icon name="check" />{t('product.photo.main')}</span>
                  ) : mayEdit ? (
                    <button type="button" className="btn btn-ghost btn-sm" disabled={busy}
                            onClick={() => setMain.mutate(photo)}>
                      {t('product.photo.makeMain')}
                    </button>
                  ) : null}
                  {mayEdit && (
                    <span className="row" style={{ gap: 2 }}>
                      <button type="button" className="btn btn-ghost btn-sm btn-icon" disabled={busy || index === 0}
                              aria-label={t('product.photo.left', { name: photo.fileName })}
                              onClick={() => move(photo, -1)}>
                        <Icon name="arrow-left" size="sm" />
                      </button>
                      <button type="button" className="btn btn-ghost btn-sm btn-icon"
                              disabled={busy || index === photos.length - 1}
                              aria-label={t('product.photo.right', { name: photo.fileName })}
                              onClick={() => move(photo, 1)}>
                        <Icon name="arrow-right" size="sm" />
                      </button>
                      <button type="button" className="btn btn-ghost btn-sm btn-icon" disabled={busy}
                              aria-label={t('product.photo.delete', { name: photo.fileName })}
                              onClick={() => setRemoving(photo)}>
                        <Icon name="trash" size="sm" />
                      </button>
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      <ConfirmModal
        open={removing != null}
        title={t('product.photo.deleteTitle')}
        confirmLabel={t('product.photo.deleteConfirm')}
        pending={remove.isPending}
        onCancel={() => setRemoving(null)}
        onConfirm={() => removing && remove.mutate(removing)}
      >
        {removing && (
          <p>
            {removing.main ? t('product.photo.deleteMainText', { name: removing.fileName })
              : t('product.photo.deleteText', { name: removing.fileName })}
          </p>
        )}
      </ConfirmModal>
    </div>
  )
}

/**
 * Одно фото или заглушка на его месте. Заглушка называет причину: «проверяется» — фото
 * будет, «не прошло проверку» — не будет; битой картинки на экране не бывает.
 */
export function PhotoFrame({ photo, large, compact }: { photo: ProductPhoto | null; large?: boolean; compact?: boolean }) {
  const { t } = useApp()
  const { url, failed } = usePhotoUrl(photo)
  const classes = ['pg-frame', large ? 'is-large' : '', compact ? 'is-compact' : ''].filter(Boolean).join(' ')
  if (photo == null) {
    return (
      <span className={classes} aria-label={t('product.photo.none')} role="img">
        <Icon name="box" />
      </span>
    )
  }
  if (url) {
    return <img className={classes} src={url} alt={photo.fileName} />
  }
  const state = photo.scanStatus === 'infected' || photo.scanStatus === 'failed' || failed
    ? 'rejected'
    : photo.downloadable ? 'loading' : 'pending'
  return (
    <span className={`${classes} is-${state}`} role="img" aria-label={t(`product.photo.state.${state}`)}>
      <Icon name={state === 'rejected' ? 'alert' : 'clock'} />
      {!compact && <span className="t-sm">{t(`product.photo.state.${state}`)}</span>}
    </span>
  )
}
